import styles from '../cartcheckout.module.scss';
import { CartContainerProps, CartItem, OBSObjI } from '@msc-b2c/interfaces/types';
import { CartHeader } from '../components/CartHeader/CartHeader';
import { CartBody } from '../components/CartBody/CartBody';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearAppliedECoupon,
  getActiveSession,
  getCurrencyCode,
  prepaidConfirmation,
  replacePlaceholders,
  RootState,
  setAppliedECoupon,
  updateCartItems,
  updateCartPrices,
  useGetBookingQuery,
} from '@msc-b2c/shared-utils';
import { skipToken } from '@reduxjs/toolkit/query';
import { EmptyCart } from '../components/EmptyCart/EmptyCart';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getPricesCart } from '../utils/getPricesCart';
import { Button, Modal, Text, Toaster } from '@msc-b2c/shared-components';
import { triggerViewCartEvent } from '../../utils/myAreaAnalytics';
import { HeadsUpIconAlignment, LoginMode, OBSTypesEnum, PageType } from '@msc-b2c/interfaces/enums';
import HeadsUpInfo, {
  HeadsUpMessagePriority,
} from '../../postbookingresults/components/drawercontent-subcomponents/HeadsUpInfo';

export function CartContainer(props: Readonly<CartContainerProps>) {
  const {
    items: cart,
    prices,
    appliedECoupon,
  } = useSelector((state: RootState) => state.myAreaCart);
  const dispatch = useDispatch();
  const [showToaster, setShowToaster] = useState(false);
  const [toasterItemCode, setToasterItemCode] = useState<string | null>(null);
  const [toasterItemDescription, setToasterItemDescription] = useState<string | null>(null);
  const [showEditSuccessToaster, setShowEditSuccessToaster] = useState(false);
  const [editedDiningPackage, setEditedDiningPackage] = useState<string | undefined>(undefined);
  const [isPricesLoading, setIsPricesLoading] = useState(false);
  const [showECouponRemovalModal, setShowECouponRemovalModal] = useState(false);
  const [pendingECouponRemoval, setPendingECouponRemoval] = useState<{
    itemCode: string;
    itemDescription: string;
    startDate?: string;
  } | null>(null);
  const deleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingDeleteItemCodeRef = useRef<{ code: string; startDate?: string } | null>(null);
  const deletedItemsRef = useRef<CartItem[]>([]);
  const removedCouponRef = useRef<string | null>(null);
  const cartItemsRef = useRef(cart);
  const previousViewCartTrackingKeyRef = useRef('');

  const onboardServices = useSelector(
    (state: RootState) => state.myCruise.obs?.onboardServices || []
  );
  const excursions = useSelector((state: RootState) => state.myCruise.excursions?.excursions || []);
  const { membershipData, cabinDetails } = useSelector((state: RootState) => state.myCruise);
  const { alreadyPurchasedSectionRouteMapping } = useSelector(
    (state: RootState) => state.navigationMenuData
  );
  const { bookingNumber = '', loginMode } = getActiveSession();
  const { data: booking } = useGetBookingQuery(bookingNumber || skipToken);
  const showTimeslotsUnavailableBanner = cart.some(
    (item) =>
      item.itemType === 'ONBOARD_SERVICE' &&
      item.type === OBSTypesEnum.RESTAURANTS &&
      Boolean(
        item.timeslotsSelectionNotAvailable || item.diningSelection?.timeslotsSelectionNotAvailable
      )
  );

  const timeslotsUnavailableBannerTitle = props.diningTimeslotsUnavailableBannerTitle ?? '';
  const timeslotsUnavailableBannerBody = props.diningTimeslotsUnavailableBannerBody ?? '';

  // Keep ref in sync with cart
  useEffect(() => {
    cartItemsRef.current = cart;
  }, [cart]);

  // reset confirmation data when land on the cart
  useEffect(() => {
    dispatch(prepaidConfirmation(null));
  }, []);

  const enrichedCartItems = useMemo(() => {
    const excursionsByCode = new Map(excursions.map((excursion) => [excursion.code, excursion]));
    const onboardServicesByCode = new Map(
      onboardServices.map((onboardService) => [onboardService.code, onboardService])
    );
    return cart
      .map((cartItem) => {
        const card =
          cartItem.itemType === 'EXCURSION'
            ? excursionsByCode.get(cartItem.code)
            : onboardServicesByCode.get(cartItem.code);
        if (!card) return undefined;
        const itemCategory2 =
          cartItem.itemType === 'EXCURSION'
            ? 'excursions'
            : alreadyPurchasedSectionRouteMapping
                ?.find((routeItem) =>
                  Object.keys(routeItem.routeTypes).includes((card as OBSObjI).type?.code ?? '')
                )
                ?.routeLabel.toLowerCase() ?? '';
        return {
          ...card,
          itemCategory2,
          itemListName: cartItem.itemListName,
          selectedPassengerQuantity: cartItem.passengers?.length,
        };
      })
      .filter((card) => !!card);
  }, [cart, excursions, onboardServices, alreadyPurchasedSectionRouteMapping]);

  const viewCartTrackingKey = useMemo(() => {
    if (!booking || !prices || enrichedCartItems.length === 0) {
      return '';
    }

    return [...(prices.excursions ?? []), ...(prices.onboardServices ?? [])]
      .map((item) => `${item.code ?? ''}:${item.price?.total?.afterDiscount ?? ''}`)
      .sort()
      .join('|');
  }, [booking, prices, enrichedCartItems]);

  useEffect(() => {
    if (
      !booking ||
      !prices ||
      enrichedCartItems.length === 0 ||
      !viewCartTrackingKey ||
      (loginMode === LoginMode.FULL && !membershipData) ||
      viewCartTrackingKey === previousViewCartTrackingKeyRef.current
    ) {
      return;
    }

    triggerViewCartEvent({
      booking,
      items: enrichedCartItems,
      cabinDetails,
      currency: getCurrencyCode(),
      value: prices?.price?.total?.afterDiscount || '0',
      membershipData,
    });
    previousViewCartTrackingKeyRef.current = viewCartTrackingKey;
  }, [
    booking,
    prices,
    enrichedCartItems,
    cabinDetails,
    viewCartTrackingKey,
    membershipData,
    loginMode,
  ]);

  const getRelatedItemCodes = useCallback((cartItems: CartItem[], seedCode: string) => {
    const codesToRemove = new Set<string>();
    const queue: string[] = [seedCode];

    while (queue.length > 0) {
      const code = queue.pop() as string;
      if (codesToRemove.has(code)) continue;
      codesToRemove.add(code);

      cartItems.forEach((item) => {
        if (item.code === code && item.relatedItemCode) {
          queue.push(item.relatedItemCode);
        }
        if (item.relatedItemCode === code) {
          queue.push(item.code);
        }
      });
    }

    return codesToRemove;
  }, []);

  const removeItemsFromCart = useCallback((itemCode: string, startDate?: string) => {
    const currentCart = cartItemsRef.current;
    const makeKey = (code: string, sd?: string) => `${code}::${sd ?? ''}`;
    const visitedKeys = new Set<string>();

    const queue = currentCart.filter(
      (item) => item.code === itemCode && (startDate === undefined || item.startDate === startDate)
    );

    while (queue.length > 0) {
      const current = queue.pop();
      if (!current) break;
      const key = makeKey(current.code, current.startDate);
      if (visitedKeys.has(key)) continue;
      visitedKeys.add(key);

      if (current.relatedItemCode) {
        const related = currentCart.find(
          (i) => i.code === current.relatedItemCode && i.startDate === current.startDate
        );
        if (related && !visitedKeys.has(makeKey(related.code, related.startDate))) {
          queue.push(related);
        }
      }

      // Follow relatedItemCode backward (items pointing to current)
      currentCart.forEach((item) => {
        if (
          item.relatedItemCode === current.code &&
          item.startDate === current.startDate &&
          !visitedKeys.has(makeKey(item.code, item.startDate))
        ) {
          queue.push(item);
        }
      });
    }

    const removedItems = currentCart.filter((item) =>
      visitedKeys.has(makeKey(item.code, item.startDate))
    );
    const updatedCart = currentCart.filter(
      (item) => !visitedKeys.has(makeKey(item.code, item.startDate))
    );

    return { updatedCart, removedItems };
  }, []);

  const executeDeletion = useCallback(
    async (itemCode: string, startDate?: string): Promise<CartItem[]> => {
      const { bookingNumber = '', cruiseId } = getActiveSession();
      const { updatedCart, removedItems } = removeItemsFromCart(itemCode, startDate);
      dispatch(updateCartItems(updatedCart));

      if (cruiseId && bookingNumber && updatedCart.length > 0) {
        setIsPricesLoading(true);
        try {
          await getPricesCart(dispatch, cruiseId, bookingNumber, false);
        } finally {
          setIsPricesLoading(false);
        }
      }

      if (updatedCart.length === 0) {
        dispatch(updateCartPrices(null));
      }
      return removedItems;
    },
    [dispatch, removeItemsFromCart]
  );

  const restoreDeletedItems = useCallback(() => {
    if (deletedItemsRef.current.length > 0) {
      const makeKey = (code: string, sd?: string) => `${code}::${sd ?? ''}`;
      const existingKeys = new Set(
        cartItemsRef.current.map((item) => makeKey(item.code, item.startDate))
      );
      const itemsToRestore = deletedItemsRef.current.filter(
        (item) => !existingKeys.has(makeKey(item.code, item.startDate))
      );
      const updatedCart = [...cartItemsRef.current, ...itemsToRestore];
      dispatch(updateCartItems(updatedCart));
      const { bookingNumber = '', cruiseId } = getActiveSession();
      if (cruiseId && bookingNumber) {
        setIsPricesLoading(true);
        getPricesCart(dispatch, cruiseId, bookingNumber, false)
          .then(() => {
            setIsPricesLoading(false);
          })
          .catch(() => {
            setIsPricesLoading(false);
          });
      }
      deletedItemsRef.current = [];
    }
  }, [dispatch]);

  const getCouponAppliedItemCodes = useCallback((): Set<string> => {
    if (!appliedECoupon || !prices) return new Set();

    const hasPromotionAmount = (amount?: string) => Number(amount || 0) > 0;

    const appliedCodes = new Set<string>();

    prices.excursions?.forEach((item) => {
      const itemHasPromo = hasPromotionAmount(item.price?.total?.promotionAmount);
      const passengerMatch = item.passengers?.some((passenger) => {
        return (
          passenger.couponCode?.isApplicable &&
          !hasPromotionAmount(passenger.price?.total?.promotionAmount)
        );
      });
      const cabinMatch = item.cabins?.some((cabin) => {
        return (
          cabin.couponCode?.isApplicable && !hasPromotionAmount(cabin.price?.total?.promotionAmount)
        );
      });

      if (!itemHasPromo && (passengerMatch || cabinMatch)) {
        appliedCodes.add(item.code);
      }
    });

    prices.onboardServices?.forEach((item) => {
      const itemHasPromo = hasPromotionAmount(item.price?.total?.promotionAmount);
      const passengerMatch = item.passengers?.some((passenger) => {
        return (
          passenger.couponCode?.isApplicable &&
          !hasPromotionAmount(passenger.price?.total?.promotionAmount)
        );
      });
      const cabinMatch = item.cabins?.some((cabin) => {
        return (
          cabin.couponCode?.isApplicable && !hasPromotionAmount(cabin.price?.total?.promotionAmount)
        );
      });

      if (!itemHasPromo && (passengerMatch || cabinMatch)) {
        appliedCodes.add(item.code);
      }
    });

    return appliedCodes;
  }, [appliedECoupon, prices]);

  useEffect(() => {
    return () => {
      if (deleteTimeoutRef.current) {
        clearTimeout(deleteTimeoutRef.current);
        deleteTimeoutRef.current = null;
      }
      if (pendingDeleteItemCodeRef.current) {
        executeDeletion(
          pendingDeleteItemCodeRef.current.code,
          pendingDeleteItemCodeRef.current.startDate
        );
      }
    };
  }, [executeDeletion]);

  const handleActualDelete = useCallback((itemCode: string) => {
    setShowToaster(false);
    setToasterItemCode(null);
    setToasterItemDescription(null);
    pendingDeleteItemCodeRef.current = null;
    deletedItemsRef.current = [];
    removedCouponRef.current = null;
  }, []);

  const handleUndo = useCallback(() => {
    if (deleteTimeoutRef.current) {
      clearTimeout(deleteTimeoutRef.current);
      deleteTimeoutRef.current = null;
    }

    setShowToaster(false);
    pendingDeleteItemCodeRef.current = null;

    if (toasterItemCode) {
      if (removedCouponRef.current) {
        dispatch(setAppliedECoupon(removedCouponRef.current));
        removedCouponRef.current = null;
      }
      restoreDeletedItems();
      setToasterItemCode(null);
      setToasterItemDescription(null);
    }
  }, [dispatch, toasterItemCode, restoreDeletedItems]);

  const handleToasterClose = useCallback(() => {
    if (deleteTimeoutRef.current) {
      clearTimeout(deleteTimeoutRef.current);
      deleteTimeoutRef.current = null;
    }
    if (toasterItemCode) {
      handleActualDelete(toasterItemCode);
    }
  }, [toasterItemCode, handleActualDelete]);

  const performItemDelete = useCallback(
    (itemCode: string, itemDescription: string, startDate?: string) => {
      const itemToDelete = cartItemsRef.current.find(
        (item) =>
          item.code === itemCode && (startDate === undefined || item.startDate === startDate)
      );
      if (!itemToDelete) return;

      pendingDeleteItemCodeRef.current = { code: itemCode, startDate };
      executeDeletion(itemCode, startDate)
        .then((removedItems) => {
          deletedItemsRef.current = removedItems;
        })
        .catch(() => {
          deletedItemsRef.current = [];
        })
        .finally(() => {
          setToasterItemCode(itemCode);
          setToasterItemDescription(itemDescription);
          setShowToaster(true);

          deleteTimeoutRef.current = setTimeout(() => {
            handleActualDelete(itemCode);
          }, 10000);
        });
    },
    [executeDeletion, handleActualDelete]
  );

  const handleItemDelete = useCallback(
    (itemCode: string, itemDescription: string, startDate?: string) => {
      const couponAppliedCodes = getCouponAppliedItemCodes();
      if (couponAppliedCodes.size > 0) {
        const codesToRemove = getRelatedItemCodes(cartItemsRef.current, itemCode);
        const remainingCouponCodes = Array.from(couponAppliedCodes).filter(
          (code) => !codesToRemove.has(code)
        );
        if (remainingCouponCodes.length === 0) {
          setPendingECouponRemoval({ itemCode, itemDescription, startDate });
          setShowECouponRemovalModal(true);
          return;
        }
      }
      performItemDelete(itemCode, itemDescription, startDate);
    },
    [appliedECoupon, getCouponAppliedItemCodes, getRelatedItemCodes, performItemDelete]
  );

  const handleConfirmECouponRemoval = useCallback(() => {
    if (!pendingECouponRemoval) return;
    const { itemCode, itemDescription, startDate } = pendingECouponRemoval;
    setShowECouponRemovalModal(false);
    setPendingECouponRemoval(null);
    if (appliedECoupon) {
      removedCouponRef.current = appliedECoupon;
      dispatch(clearAppliedECoupon());
    }
    performItemDelete(itemCode, itemDescription, startDate);
  }, [appliedECoupon, dispatch, pendingECouponRemoval, performItemDelete]);

  const handleCancelECouponRemoval = useCallback(() => {
    setShowECouponRemovalModal(false);
    setPendingECouponRemoval(null);
  }, []);

  const renderToaster = () => {
    let message;
    let onClose: () => void;
    let understoodText = undefined;
    let onUnderstood = undefined;
    let bgColor = '#34343D';

    const isDiningToaster =
      showEditSuccessToaster &&
      !!props.diningEditSuccessToasterText &&
      !!props.diningEditSuccessToasterBgColor;

    if (showToaster && !isPricesLoading) {
      message = replacePlaceholders(props.removeItemToasterLabel || '', {
        item: toasterItemDescription || '',
      });
      onClose = handleToasterClose;
      understoodText = props.undoRemovalButtonLabel;
      onUnderstood = handleUndo;
    } else if (isDiningToaster) {
      message = replacePlaceholders(props.diningEditSuccessToasterText ?? '', {
        item: editedDiningPackage ?? '',
      });
      onClose = () => {
        setShowEditSuccessToaster(false);
        setEditedDiningPackage(undefined);
      };
      bgColor = props.diningEditSuccessToasterBgColor;
    } else {
      return null;
    }

    return (
      <Toaster
        ToasterMessage={message}
        isAutoClosing={true}
        autoClosingDelay={10000}
        onClose={onClose}
        closeButton={true}
        displayIcon={false}
        position="bottom-center"
        type="relative"
        displayActions={true}
        understoodText={understoodText}
        onUnderstood={onUnderstood}
        backgroundColor={bgColor}
        iconColor="#FFFFFF"
        styleOptions={styles['cart-item-deletion-toaster']}
      />
    );
  };

  return (
    <div className={styles['cart-checkout-container']}>
      <CartHeader
        {...props}
        title={props.cartContainerTitle}
        items={cart}
        showItemsCount={true}
        bannerContent={
          showTimeslotsUnavailableBanner &&
          (timeslotsUnavailableBannerTitle || timeslotsUnavailableBannerBody) ? (
            <HeadsUpInfo
              icon={props.diningTimeslotsUnavailableBannerIcon ?? ''}
              title={timeslotsUnavailableBannerTitle}
              description={timeslotsUnavailableBannerBody ?? ''}
              priority={HeadsUpMessagePriority.DEFAULT}
              visibilityCondition={() => true}
              bgColor={props.diningTimeslotsUnavailableBannerBgColor}
              fontColor={props.diningTimeslotsUnavailableBannerFontColor}
              iconAlignment={HeadsUpIconAlignment.TOP}
              closeIcon="close"
            />
          ) : undefined
        }
      />

      {cart.length > 0 ? (
        <CartBody
          {...props}
          items={cart}
          onItemDelete={handleItemDelete}
          isPricesLoading={isPricesLoading}
          pageType={PageType.CART_PAGE}
          setEditedDiningPackage={setEditedDiningPackage}
          editedDiningPackage={editedDiningPackage}
          showEditSuccess={() => setShowEditSuccessToaster(true)}
        />
      ) : (
        <EmptyCart labels={props} />
      )}
      {renderToaster()}
      {showECouponRemovalModal && pendingECouponRemoval && (
        <Modal
          id="ecoupon-removal-modal"
          styleOption={'-modal-small'}
          onClose={handleCancelECouponRemoval}
          headerTitle={props.ecouponRemovalModalTitle}
          modalIcon={props.ecouponRemovalModalCloseIcon}
          shouldRenderBackdrop={Boolean(props.ecouponRemovalModalCloseIcon)}
        >
          <div className={styles['cart-ecoupon-removal-modal']}>
            <Text
              text={replacePlaceholders(props.ecouponRemovalModalDescription || '', {
                itemName: pendingECouponRemoval.itemDescription,
              })}
            />
            <div className={styles['cart-ecoupon-removal-modal__actions']}>
              <Button
                text={props.ecouponRemovalModalCancelCta}
                styleOption="cmp-button__secondary"
                onClick={handleCancelECouponRemoval}
              />
              <Button
                text={props.ecouponRemovalModalConfirmationCta}
                styleOption="cmp-button__primary"
                onClick={handleConfirmECouponRemoval}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
